import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const PageStack = styled(motion.div, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
})

const BackLink = styled(motion.button, {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    alignSelf: 'flex-start',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: {
      color: 'quickpass.primary',
    },
  },
})

const HeroSection = styled('section', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '20px',
    md: {
      gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
    },
  },
})

const HeroImage = styled('div', {
  base: {
    width: '100%',
    height: '320px',
    borderRadius: 'lg',
    backgroundColor: 'quickpass.surfaceCool',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow: '0 18px 40px -22px rgba(128, 84, 255, 0.28)',
    position: 'relative',
    overflow: 'hidden',
  },
})

const HeroPlaceholder = styled('div', {
  base: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '64px',
    color: 'quickpass.primarySoft',
  },
})

const SiteBadge = styled('span', {
  base: {
    position: 'absolute',
    top: '14px',
    left: '14px',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    paddingX: '12px',
    paddingY: '6px',
    borderRadius: 'pill',
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    color: 'quickpass.ink',
    backdropFilter: 'blur(2px)',
  },
})

const HeroSummary = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const TitleBlock = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const Title = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '28px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.5px',
    lineHeight: '1.15',
  },
})

const Address = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const PriceRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '14px',
    flexWrap: 'wrap',
  },
})

const Price = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '34px',
    fontWeight: '800',
    color: 'quickpass.primary',
    letterSpacing: '-0.6px',
  },
})

const PriceTrendPill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    paddingX: '10px',
    paddingY: '4px',
    borderRadius: 'pill',
  },
  variants: {
    direction: {
      up: { backgroundColor: 'quickpass.warmth', color: '#FFFFFF' },
      down: { backgroundColor: 'quickpass.success', color: '#FFFFFF' },
      flat: {
        backgroundColor: 'quickpass.surfaceCool',
        color: 'quickpass.inkSoft',
      },
    },
  },
})

const MetaRow = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
})

const MetaDot = styled('span', {
  base: {
    color: '#D8D2EC',
  },
})

const ActionRow = styled('div', {
  base: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
})

const PrimaryAction = styled('a', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    paddingX: '18px',
    paddingY: '10px',
    borderRadius: 'pill',
    textDecoration: 'none',
    cursor: 'pointer',
    border: 'none',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.5)',
    },
  },
})

const SecondaryAction = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '9px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primary',
      color: 'quickpass.primary',
    },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: {
        borderColor: '#E5E0F5',
        color: 'quickpass.inkSoft',
      },
    },
  },
  variants: {
    tone: {
      neutral: {},
      danger: {
        _hover: {
          borderColor: 'quickpass.warmth',
          color: 'quickpass.warmth',
        },
      },
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
})

const Section = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px 22px',
    boxShadow: '0 12px 32px -22px rgba(128, 84, 255, 0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const SectionHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const SectionTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.2px',
  },
})

const SectionStamp = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const PriceHeadline = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const Description = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.55',
    whiteSpace: 'pre-wrap',
  },
})

const TimelineList = styled('ul', {
  base: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const TimelineRow = styled('li', {
  base: {
    display: 'grid',
    gridTemplateColumns: '94px 1fr',
    gap: '14px',
    alignItems: 'baseline',
  },
})

const TimelineStamp = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
  },
})

const TimelineLabel = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
})

const ImageGallery = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '10px',
  },
})

const GalleryImage = styled('div', {
  base: {
    width: '100%',
    height: '120px',
    borderRadius: 'md',
    backgroundColor: 'quickpass.surfaceCool',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
})

const NotFoundCard = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '40px 32px',
    textAlign: 'center',
    border: '1.5px dashed',
    borderColor: '#E5E0F5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
})

const NotFoundEmoji = styled('span', {
  base: {
    fontSize: '40px',
    lineHeight: '1',
  },
})

const NotFoundTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const NotFoundBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '420px',
  },
})

const PersonalSection = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px 22px',
    boxShadow: '0 12px 32px -22px rgba(128, 84, 255, 0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const PersonalHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const PersonalLabel = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
  },
})

const NoteEditor = styled('textarea', {
  base: {
    width: '100%',
    minHeight: '120px',
    padding: '12px 14px',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    resize: 'vertical',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, background-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
    _placeholder: {
      color: 'quickpass.inkSoft',
      fontStyle: 'italic',
    },
  },
})

const NoteSavedHint = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '600',
    color: 'quickpass.success',
    fontStyle: 'italic',
  },
})

const ScrapeWarningBlock = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.warmth',
    backgroundColor: 'rgba(255, 139, 115, 0.08)',
    borderLeft: '3px solid',
    borderColor: 'quickpass.warmth',
    paddingX: '14px',
    paddingY: '10px',
    borderRadius: 'sm',
  },
})

export {
  PageStack,
  BackLink,
  HeroSection,
  HeroImage,
  HeroPlaceholder,
  SiteBadge,
  HeroSummary,
  TitleBlock,
  Title,
  Address,
  PriceRow,
  Price,
  PriceTrendPill,
  MetaRow,
  MetaDot,
  ActionRow,
  PrimaryAction,
  SecondaryAction,
  Section,
  SectionHeader,
  SectionTitle,
  SectionStamp,
  PersonalSection,
  PersonalHeader,
  PersonalLabel,
  NoteEditor,
  NoteSavedHint,
  PriceHeadline,
  Description,
  TimelineList,
  TimelineRow,
  TimelineStamp,
  TimelineLabel,
  ImageGallery,
  GalleryImage,
  NotFoundCard,
  NotFoundEmoji,
  NotFoundTitle,
  NotFoundBody,
  ScrapeWarningBlock,
}
